// @ts-strict-ignore
import { useState } from "react";

import { AuthFooter } from "@shared/components/AuthFooter/AuthFooter";
import { AuthHeader } from "@shared/components/AuthHeader/AuthHeader";
import { AuthHeaderWrapper } from "@shared/components/AuthHeaderWrapper/AuthHeaderWrapper";
import { useLocation } from "@shared/react-router-dom/react-router-dom";
import {
  type StorePlatform as StorePlatformType,
  StorePlatform,
} from "@shared/types/StorePlatform";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import type { PatientAttributes } from "@unauthenticated/shared/data/types/PatientAttributes";
import { LinkExpiredNotificationPage } from "@unauthenticated/signupSignin/components/LinkExpiredNotificationPage/LinkExpiredNotificationPage";
import { LinkNotificationPage } from "@unauthenticated/signupSignin/components/LinkNotificationPage/LinkNotificationPage";
import { Page } from "@unauthenticated/signupSignin/components/Page/Page";
import { PageContent } from "@unauthenticated/signupSignin/components/PageContent/PageContent";
import { StoreLandingLayout } from "@unauthenticated/signupSignin/components/StoreLandingLayout/StoreLandingLayout";
import { EmailFormContent } from "@unauthenticated/signupSignin/scenes/ContinueWithoutPasswordPage/EmailFormContent/EmailFormContent";
import { SuccessContent } from "@unauthenticated/signupSignin/scenes/ContinueWithoutPasswordPage/SuccessContent/SuccessContent";
import { MultiplatformNotificationPage } from "@unauthenticated/signupSignin/scenes/MultiplatformNotificationPage/MultiplatformNotificationPage";

import * as styles from "./ContinueWithoutPassword.styles";

interface HistoryState {
  source?: "RX_SIGN_IN";
  email?: string;
  page?: "success";
}

type Props =
  | {
      isStorefronts?: never;
      patientAttributes?: never;
    }
  | {
      isStorefronts: boolean;
      patientAttributes: PatientAttributes;
    };

const ContinueWithoutPasswordPage = ({ isStorefronts, patientAttributes }: Props) => {
  const [email, setEmail] = useState(undefined);
  const { state }: { state: HistoryState } = useLocation();
  const [page, setPage] = useState<"email" | "success">(state?.page || "email");
  const isSwiftFillSignIn = state?.source === "RX_SIGN_IN";

  const { search } = useLocation();

  const linkEmail = new URLSearchParams(search).get("email");
  const linkHasError = new URLSearchParams(search).get("has_error") === "true";
  const linkStorePlatform = new URLSearchParams(search).get(
    "enable_shared_account"
  ) as StorePlatformType;
  const migrateHarmonizedAccount = new URLSearchParams(search).get("migrate_harmonized_account");
  const isTwoFactorAuthenticationEnabled =
    new URLSearchParams(search).get("two_factor_authentication") === "true";

  if (linkEmail && linkStorePlatform) {
    if (linkHasError) {
      return <LinkExpiredNotificationPage email={linkEmail} storePlatform={linkStorePlatform} />;
    }

    return <LinkNotificationPage email={linkEmail} storePlatform={linkStorePlatform} />;
  }

  if (linkEmail && migrateHarmonizedAccount) {
    if (linkHasError) {
      return (
        <LinkExpiredNotificationPage
          email={linkEmail}
          storePlatform={StorePlatform.EMERSON}
          migrateHarmonizedAccount={!!migrateHarmonizedAccount}
        />
      );
    }

    return (
      <MultiplatformNotificationPage
        email={linkEmail}
        isTwoFactorAuthenticationEnabled={isTwoFactorAuthenticationEnabled}
      />
    );
  }

  const renderContent = () => {
    if (page === "email") {
      return <EmailFormContent email={email} setEmail={setEmail} setPage={setPage} />;
    } else {
      return (
        <SuccessContent
          email={state?.email || email}
          setPage={setPage}
          isSwiftFillSignIn={isSwiftFillSignIn}
          isStorefronts={isStorefronts}
        />
      );
    }
  };

  if (isStorefronts) {
    const { storeName, pracAvatar, organizationLogo } = patientAttributes;

    return (
      <StoreLandingLayout
        storeName={storeName}
        organizationLogo={organizationLogo}
        storeId=""
        pracAvatar={pracAvatar}
        isActivePromotion={false}
        isStorefronts
      >
        <div css={styles.contentLayout}>{renderContent()}</div>
      </StoreLandingLayout>
    );
  }

  return (
    <Page>
      <PageContent>
        <AuthHeaderWrapper>
          <AuthHeader route={authRoutes.login} />
        </AuthHeaderWrapper>
        <div css={styles.contentLayout}>{renderContent()}</div>
      </PageContent>
      <AuthFooter />
    </Page>
  );
};

export { ContinueWithoutPasswordPage };
